import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Navbar.module.css';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { Skeleton } from '@/components';

import { useGlobalStore } from '@/stores';

// TODO: Add when preview is implemented
// import { useStoresStore } from '@/stores/stores';
// import MobileStorePreviewOverlay from '../MobileStorePreviewOverlay/MobileStorePreviewOverlay';
// import StorePreview from '../StorePreview/StorePreview';

// Local hooks
import { useAuthActions, useNavigation, useUrlDisplay, useWindowWidth } from './Navbar.hooks';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions';

// Icons
import _CopyIcon from '@/assets/icons/ic-copy.svg';
import _LinkIcon from '@/assets/icons/ic-link.svg';
import _LeftArrowIcon from '@/assets/icons/ic-arrow-left.svg';
import _MobileIcon from '@/assets/icons/ic-mobile-phone.svg';
import { configSettings, createIconElement, stanAnalytics } from '@/lib';
import { useUser } from '@/queries/useUser';
// import { useStoreStore } from '@/stores/storeStore';

interface NavbarProps {
    show?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ show = true }) => {
    const location = useLocation();
    const pathname = location.pathname;
    const storePreviewOverlayRef = useRef<any>(null);
    const { isSmallerThanTablet } = useWindowWidth();

    const { user, isUserLoading } = useUser();
    // If user status is 0, we log out here
    useAuthActions(user);

    const getPageURL = useGlobalStore(state => state.pageURL);
    const showProductEditorNavigation = useGlobalStore(state => state.showProductEditorNavigation);
    const showProductEditorPreview = useGlobalStore(state => state.showProductEditorPreview);
    const showPreview = useGlobalStore(state => state.showPreview);
    const setShowNavigation = useGlobalStore(state => state.setShowNavigation);
    const setShowProductEditorPreview = useGlobalStore(state => state.setShowProductEditorPreview);

    // TODO: Add back when preview is implemented
    // const { storeId, pagesWithSections } = useStoreStore();

    // TODO: Validate assumption: siteSettings is available globally or through a hook
    const storeUrl = `${configSettings.frontDomain}${user?.username}`;

    const { displayUrl, removeHttps, copyTextToClipboard } = useUrlDisplay(storeUrl, getPageURL);

    // Track analytics when copy button is clicked
    const handleCopyClick = async () => {
        await copyTextToClipboard();
        stanAnalytics('link-shared', {
            meta: { user_id: user?.user_id, username: user?.username },
        });
    };

    const isAffiliateOnly = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;

    const notNewUserHoldout = !user?.experiments?.new_user_hold_out;
    const hasProductEditor2025 =
        user?.experiments?.digital_product_editor_v2 === 1 || user?.experiments?.digital_product_editor_v3 === 1;

    const storePreviewAvailable =
        notNewUserHoldout && (pathname.includes('/linksites') || pathname.includes('/linksite_add_page'));
    const sbChallengeExperiment = user?.experiments?.steven_bartlett_challenge_experiment;
    const productEditorPreviewAvailable = notNewUserHoldout || hasProductEditor2025 || sbChallengeExperiment;

    // Navigation hook (after storePreviewAvailable is defined)
    const { togglePreview, goBack, handleBreadcrumbNavigation } = useNavigation(
        showProductEditorPreview,
        setShowNavigation,
        setShowProductEditorPreview,
        storePreviewOverlayRef,
        storePreviewAvailable
    );

    // TODO: Add back when preview is implemented
    // const { sectionWithPages } = usePagesProcessing(getStorePagesWithSections, getStoreId, CONSTANTS, helpers);
    // useEffect(() => {
    //     if (showPreview) {
    //         // TODO: Validate assumption: jQuery equivalent for scroll animation
    //         const previewElement = document.querySelector('.mobile-preview-wrapper .site__prev__masking');
    //         if (previewElement) {
    //             previewElement.scrollTop = 0;
    //         }
    //     }
    // }, [showPreview]);

    if (!show) {
        return null;
    }

    const CopyIcon = createIconElement(_CopyIcon, { className: styles.copyIcon });
    const LinkIcon = createIconElement(_LinkIcon, { className: styles.copyIcon });
    const LeftArrowIcon = createIconElement(_LeftArrowIcon, { className: styles.backIcon });
    const MobileIcon = createIconElement(_MobileIcon, { className: styles.previewIcon });

    return (
        <div className={`${styles.navbarWrapper} ${showPreview ? styles.previewActive : ''}`}>
            {/* {isAdminLoggedInAsUser && user && (
                <div className={styles.adminWarning}>
                    Temporarily logged in as user [{user.user_id}] [{user.username}]
                </div>
            )} */}

            {productEditorPreviewAvailable && showProductEditorNavigation && isSmallerThanTablet && user && (
                <nav className={styles.navbar}>
                    <div onClick={goBack} className={styles.backButton}>
                        {LeftArrowIcon}
                    </div>
                    <div onClick={togglePreview} className={styles.previewButton}>
                        <span>Preview</span>
                        {MobileIcon}
                    </div>
                </nav>
            )}

            {!(productEditorPreviewAvailable && showProductEditorNavigation && isSmallerThanTablet && user) && (
                <nav className={styles.navbar}>
                    {/* Always render breadcrumb container to prevent layout shift */}
                    <div className={styles.breadcrumbContainer}>
                        <Breadcrumb onActiveTab={handleBreadcrumbNavigation} />
                    </div>

                    {/* Share Link and Preview Section - Always render container to prevent layout shift */}
                    <div className={styles.shareSection}>
                        {isUserLoading ? (
                            /* Skeleton while user data is loading */
                            <>
                                <Skeleton className='hidden h-5 w-32 lg:block' />
                                <Skeleton className='size-5 lg:hidden' />
                                <Skeleton className='ml-2 size-5' />
                            </>
                        ) : user && user.username ? (
                            <>
                                {/* Mobile Preview Button */}
                                {storePreviewAvailable && isSmallerThanTablet && (
                                    <div onClick={togglePreview} className={styles.previewButton}>
                                        <span>Preview</span>
                                        {MobileIcon}
                                    </div>
                                )}

                                {!isAffiliateOnly && (
                                    <>
                                        <p className={styles.storeUrlContainer}>
                                            <a href={displayUrl} target='_blank' rel='noopener noreferrer'>
                                                {removeHttps(displayUrl)}
                                            </a>
                                        </p>
                                        <a
                                            href={displayUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className={styles.mobileStoreLink}
                                        >
                                            {LinkIcon}
                                        </a>
                                    </>
                                )}

                                {!isAffiliateOnly && (
                                    <button
                                        className={styles.shareButtonNavbar}
                                        onClick={handleCopyClick}
                                        aria-label='Copy link to clipboard'
                                        type='button'
                                    >
                                        {CopyIcon}
                                    </button>
                                )}
                            </>
                        ) : null}
                    </div>
                </nav>
            )}

            {/* TODO: Mobile Store Preview Overlay
            {storePreviewAvailable && (
                <MobileStorePreviewOverlay ref={storePreviewOverlayRef}>
                    <StorePreview
                        user={user}
                        sections={sectionWithPages}
                        className='mobile-preview'
                        features={getStoreFeatures}
                    />
                </MobileStorePreviewOverlay>
            )} */}
        </div>
    );
};

export default Navbar;
